import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';

import { gqlClient } from '@/api';

/**
 * Personal API tokens: how an assistant (Claude Code, a script) signs in to
 * the MCP endpoint, since it has no browser to sign in with. Made here, from
 * a signed-in session, with the password typed again; shown once; revoked
 * from the same list.
 */

export const API_TOKEN_SCOPES = [
  'apps:read',
  'apps:write',
  'posts:read',
  'posts:write',
  'files:write',
  'translate',
] as const;
export type ApiTokenScope = (typeof API_TOKEN_SCOPES)[number];

/**
 * The scopes a token can be made with today. `translate` exists on the
 * server for what comes next; a box for a scope no tool honours yet would be
 * a promise the endpoint does not keep.
 */
export const OFFERED_SCOPES: readonly ApiTokenScope[] = [
  'apps:read',
  'apps:write',
  'posts:read',
  'posts:write',
  'files:write',
];

/** What each scope lets a token do, for the checkboxes. */
export const SCOPE_LABELS: Record<ApiTokenScope, string> = {
  'apps:read': 'Read apps',
  'apps:write': 'Change and publish apps',
  'posts:read': 'Read posts',
  'posts:write': 'Write and publish posts',
  'files:write': 'Store pictures (covers, icons)',
  translate: 'Run translations',
};

export interface ApiToken {
  id: string;
  name: string;
  prefix: string;
  scopes: ApiTokenScope[];
  createdAt: string;
  expiresAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  /** The OAuth client holding this grant — "ChatGPT" — or null for a personal token. */
  client: string | null;
  /** When an OAuth grant ends; `expiresAt` on such a row is only the hour its access token is good for. */
  refreshExpiresAt: string | null;
}

/** The enum travels upper-cased with an underscore: `posts:write` ↔ `POSTS_WRITE`. */
export const wire = (scope: ApiTokenScope): string => scope.toUpperCase().replace(':', '_');
export const unwire = (scope: string): ApiTokenScope =>
  scope.toLowerCase().replace('_', ':') as ApiTokenScope;

const FIELDS = `id name prefix scopes createdAt expiresAt lastUsedAt revokedAt client refreshExpiresAt`;

const API_TOKENS = gql`query ApiTokens { apiTokens { ${FIELDS} } mcpEndpoint }`;
const CREATE = gql`
  mutation CreateApiToken($input: CreateApiTokenInput!) {
    createApiToken(input: $input) { token record { ${FIELDS} } }
  }
`;
const REVOKE = gql`
  mutation RevokeApiToken($id: ID!) {
    revokeApiToken(id: $id)
  }
`;

const fromWire = (token: ApiToken): ApiToken => ({ ...token, scopes: token.scopes.map(unwire) });

/** The tokens, and where they are used: the MCP endpoint, which only the API knows. */
export const useApiTokens = () =>
  useQuery({
    queryKey: ['apiTokens'],
    queryFn: async () => {
      const data = await gqlClient.request<{ apiTokens: ApiToken[]; mcpEndpoint: string }>(
        API_TOKENS,
      );
      return { tokens: data.apiTokens.map(fromWire), mcpEndpoint: data.mcpEndpoint };
    },
  });

export const useCreateApiToken = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; scopes: ApiTokenScope[]; password: string }) => {
      const { createApiToken } = await gqlClient.request<{
        createApiToken: { token: string; record: ApiToken };
      }>(CREATE, { input: { ...input, scopes: input.scopes.map(wire) } });
      return { token: createApiToken.token, record: fromWire(createApiToken.record) };
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['apiTokens'] }),
  });
};

export const useRevokeApiToken = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await gqlClient.request<{ revokeApiToken: boolean }>(REVOKE, { id })).revokeApiToken,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['apiTokens'] }),
  });
};
